'use client';
import type { ForwardedRef, ReactNode } from 'react';
import { mergeProps, useHover, useId } from 'react-aria';
import { forwardRef } from '../../forward-ref.js';
import type { CommonProps } from '../types.js';
import { useTableLoading } from './contexts/loading.js';
import { TableRowStateProvider } from './contexts/row-state.js';
import { useSelectableRow } from './hooks/use-selectable-row.js';

export interface TableBodyRowProps extends CommonProps {
  /** The cells that belong to this row. */
  children: ReactNode;
  /**
   * The row's position within the full dataset, including headers.
   *
   * Provide this value if your table presents a subset of rows, such as when using pagination.
   *
   * This number must be:
   * - greater than or equal to `1`
   * - greater than the `rowNumber` of any previous rows
   * - less than or equal to the `totalRowCount` of the parent `Table`
   *
   * Use together with `totalRowCount` on the `Table` component.
   */
  rowNumber?: number;
  /**
   * Whether the row is selected.
   *
   * This value will be ignored if `rowSelectionMode` on the `Table` component is set to `'none'`.
   */
  isSelected?: boolean;
  /**
   * A function called when the row's selection state should change.
   *
   * If called without an argument, you are free to pick the new selection state based on your own logic.
   * If called with an argument, you must set the selection state to the provided value.
   */
  onSelectionToggle?: (value?: boolean) => void;
}

function TableBodyRow(
  { isSelected, onSelectionToggle, ...props }: TableBodyRowProps,
  ref: ForwardedRef<HTMLTableRowElement>,
) {
  const rowKey = useId();

  return (
    <TableRowStateProvider rowKey={rowKey} isSelected={isSelected} onSelectionToggle={onSelectionToggle}>
      <TableBodyRowInner props={props} forwardedRef={ref} />
    </TableRowStateProvider>
  );
}

interface TableBodyRowInnerProps {
  props: Omit<TableBodyRowProps, 'isSelected' | 'onSelectionToggle'>;
  forwardedRef: ForwardedRef<HTMLTableRowElement>;
}

/**
 * Renders a row within a table body.
 *
 * See [table usage guidelines](https://ui.cimpress.io/components/table/) and [table building guide](https://ui.cimpress.io/dev-guides/tables/).
 */
const _TableBodyRow = forwardRef(TableBodyRow, 'TableBodyRow');

function TableBodyRowInner({
  props: { children, UNSAFE_className, UNSAFE_style, rowNumber, ...props },
  forwardedRef,
}: TableBodyRowInnerProps) {
  const { rowProps, isSelected, rowSelectionMode } = useSelectableRow();
  const { hoverProps, isHovered } = useHover({});
  const isLoading = useTableLoading();

  return (
    <tr
      {...mergeProps(rowProps, hoverProps, props)}
      // Workaround for React not having type definitions for the `inert` attribute
      // eslint-disable-next-line internal/no-extra-props-in-spread
      {...{ inert: isLoading ? '' : undefined }}
      ref={forwardedRef}
      className={UNSAFE_className}
      style={UNSAFE_style}
      aria-rowindex={rowNumber}
      aria-selected={rowSelectionMode !== 'none' ? (isSelected ?? false) : undefined}
      data-hovered={isHovered || undefined}
      data-selected={isSelected || undefined}
    >
      {children}
    </tr>
  );
}

export { _TableBodyRow as TableBodyRow };
